import React, { useState, useEffect, useRef } from "react";
import { ReorderableList, debounce } from "component-library";
import cx from "classnames";
import { dataTestIds } from "data-testids";
import {
  FEATURE_FLAGS,
  LaunchDarklyClientWithFlags,
  useLaunchDarklyFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { IShowreelsContent } from "./ShowreelsContent";
import { ShowreelThumb } from "./ShowreelThumb/ShowreelThumb";
import { MediaAsset } from "mediaModules/media/models";
import { ShimmeringStub } from "mediaModules/media/components/ShimmeringStub";
import { heapTrackingForShowreelsSortCompleted } from "app/helpers/heapTracking";
import "./Showreels.scss";

const MOBILE_TABLET_WIDTH = 1200;

export interface IShowreelsProps extends LaunchDarklyClientWithFlags {
  showreelsList: MediaAsset[];
  content: IShowreelsContent;
  setSelectedShowreels: (list: MediaAsset[]) => void;
  playbackVideo: (model: MediaAsset) => void;
  addNewShowreel: () => void;
  selectedShowreels: MediaAsset[];
  editShowreel: (showreel?: MediaAsset) => void;
  isMediaMetadataLoading: boolean;
  areMediaObjectsLoading: boolean;
  sortCompleted: (oldIndex: number, newIndex: number) => Promise<void>;
  disableReordering: boolean;
}

type IShowreelsComposedProps = Omit<
  IShowreelsProps,
  keyof LaunchDarklyClientWithFlags
>;

export function Showreels({
  showreelsList,
  content,
  setSelectedShowreels,
  playbackVideo,
  addNewShowreel,
  selectedShowreels,
  editShowreel,
  isMediaMetadataLoading,
  areMediaObjectsLoading,
  sortCompleted: sortCompletedProp,
  disableReordering,
}: IShowreelsProps) {
  const [disableSorting, setDisableSorting] = useState(false);
  const refsListRef = useRef<HTMLDivElement[]>([]);
  const isProfileRedesignEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.ProfileRedesign
  );

  const debouncedResizeHandler = useRef(
    debounce(() => {
      setDisableSorting(window.innerWidth < MOBILE_TABLET_WIDTH);
    }, 300)
  ).current;

  useEffect(() => {
    window.addEventListener("resize", debouncedResizeHandler, false);
    debouncedResizeHandler();

    return () => {
      window.removeEventListener("resize", debouncedResizeHandler, false);
    };
  }, [debouncedResizeHandler]);

  const startSort = () => {
    refsListRef.current.forEach((item) =>
      item.classList.remove("c-showreel-thumb--hover")
    );
  };

  const handleSortCompleted = async ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    heapTrackingForShowreelsSortCompleted(isProfileRedesignEnabled);

    await sortCompletedProp(oldIndex, newIndex);

    refsListRef.current.forEach((item) =>
      item.classList.add("c-showreel-thumb--hover")
    );
  };

  const getShowreelElements = (element: HTMLDivElement) => {
    refsListRef.current.push(element);

    return refsListRef.current;
  };

  const checkShowreel =
    (selectedShowreel: MediaAsset) =>
    (isChecked: boolean): void => {
      const newSelectedShowreels = [...selectedShowreels];
      const index = newSelectedShowreels.findIndex(
        (item) => item.id === selectedShowreel.id
      );

      if (isChecked) {
        newSelectedShowreels.push(selectedShowreel);
      } else {
        newSelectedShowreels.splice(index, 1);
      }

      setSelectedShowreels(newSelectedShowreels);
    };

  const renderShowreel = (showreel: MediaAsset) => {
    const { hiddenTitle, processingTitle, errorTitle } = content;

    return (
      <ShowreelThumb
        key={showreel.id}
        hiddenTitle={hiddenTitle}
        processingTitle={processingTitle}
        errorTitle={errorTitle}
        playbackVideo={playbackVideo}
        showreel={showreel}
        selectedShowreels={selectedShowreels}
        editShowreel={editShowreel}
        checkShowreel={checkShowreel(showreel)}
        getShowreelElements={getShowreelElements}
      />
    );
  };

  const renderAddShowreel = () => (
    <a
      className={cx("c-showreels__add-showreel", {
        "c-showreels__add-showreel--disabled": areMediaObjectsLoading,
      })}
      onClick={addNewShowreel}
      tabIndex={0}
      data-testid={dataTestIds.profile["Media.Video.AddReelButton"]}
    >
      <i className="c-showreels__add-icon icon-plus" />
      <span className="c-showreels__add-label">{content.addMediaLabel}</span>
    </a>
  );

  const renderLoadingMediaMetadata = () => (
    <div className="g-inner-grid-12 c-showreels__loading">
      <div className="g-col-lg-2 g-col-md-3 g-col-sm-12">
        <div className="c-showreels__loading-thumbnail">
          <ShimmeringStub />
        </div>
        <div className="c-showreels__loading-title">
          <ShimmeringStub />
        </div>
      </div>
    </div>
  );

  const renderLoadedMediaMetadata = () => (
    <div className="c-showreels-list__wrapper">
      <ReorderableList
        axis="xy"
        distance={5}
        list={showreelsList}
        itemRenderer={renderShowreel}
        getUniqueKey={(showreel) => showreel.id}
        immovableTail={renderAddShowreel() as any}
        onSortStart={startSort}
        onSortEnd={handleSortCompleted}
        disableReordering={disableReordering}
        shouldCancelStart={() => disableSorting}
      />
    </div>
  );

  return (
    <div className="c-showreels">
      {isMediaMetadataLoading
        ? renderLoadingMediaMetadata()
        : renderLoadedMediaMetadata()}
    </div>
  );
}

export default withLaunchDarklyConsumerHOC()(
  Showreels
) as React.ComponentType<IShowreelsComposedProps>;
